import { Menu, Search } from 'lucide-react'
import type { ReactNode } from 'react'
import { usePalette } from './PaletteContext'
import { PulsMarke } from './PulsMarke'
import { useSchublade } from './Schublade'
import { AktiveWeltMarke } from './WorkspaceMarke'

/**
 * Kopfzeile wie im Mockup: Titel, Untertitel, Suchfeld, Aktion.
 *
 * **Sie ist der Ort für den Menüknopf** ([Z-10](../../../../docs/phasen/zusatz-10-mobil.md)):
 * alle zwölf Routen benutzen diese Komponente, also erreicht ein Knopf hier jede Ansicht –
 * ohne zwölf Einzelfälle und ohne eine zweite, dauerhaft eingeblendete Leiste, die dem
 * Telefon noch einmal 50 px Höhe wegnähme.
 *
 * Unterhalb von `lg` darf die Zeile **umbrechen**: Titel und Suche stehen oben, die Aktionen
 * darunter in voller Breite. Vorher standen alle vier Dinge in einer nicht umbrechenden Reihe;
 * auf „Mein Tag" sind das Tagesziel, „+ Aufgabe" und „Was als Nächstes" – zusammen weit mehr,
 * als ein Telefon hergibt.
 */
export function Topbar({
  titel,
  untertitel,
  aktion,
  ohnePuls = false,
}: {
  titel: string
  untertitel?: ReactNode
  aktion?: ReactNode
  /** Z-22: im Puls selbst stünde die Marke doppelt – der Tab-Titel läuft trotzdem weiter. */
  ohnePuls?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5 pt-4 pb-3.5 lg:flex-nowrap lg:pt-5 lg:pb-[18px]">
      <MenueKnopf />
      {/**
       * **Titel und Datum haben einen Boden.**
       *
       * `flex-1` mit `min-w-0` darf bis auf **null** schrumpfen – und genau das tat die Spalte,
       * sobald die Aktionen breiter waren als der Platz: „Mein Tag“ verschwand im `truncate`,
       * das Datum brach in einer 0 px breiten Spalte Wort für Wort um und lief dabei über den
       * Suchknopf. Zu sehen auf „Mein Tag“ (sechs Aktionen) unterhalb von etwa 1600 px.
       *
       * Ab `lg` bekommt die Spalte deshalb 15 rem – „Mittwoch, 30. September · KW 40“ passt
       * hinein. Der Druck landet dann bei den Aktionen, und die können umbrechen; der Kopf
       * wird eine Zeile höher, aber er bleibt lesbar. Darunter hat die Spalte ohnehin die
       * ganze Breite.
       */}
      <div className="min-w-0 flex-1 lg:min-w-[15rem]">
        <div className="flex min-w-0 items-center gap-2">
          <h1 className="truncate text-[18px] font-bold tracking-[-0.3px] lg:text-[21px]">
            {titel}
          </h1>
          {/**
           * **Die aktive Arbeitswelt, unmittelbar neben dem Titel** (Nils, 26.08.2026).
           *
           * Sie entscheidet selbst, ob sie erscheint – siehe {@link AktiveWeltMarke}. Deshalb
           * steht hier keine Bedingung und in den sechs gebundenen Ansichten keine Zeile.
           */}
          <AktiveWeltMarke />
        </div>
        {untertitel && <div className="mt-px truncate text-[13px] text-ink2">{untertitel}</div>}
      </div>
      <PulsMarke versteckt={ohnePuls} />
      <SuchAnstoss />
      {/**
       * Am Telefon eine eigene Zeile, am Rechner in derselben. `w-full` erzwingt unter `lg`
       * den Umbruch – die Aktionen sind das Breiteste an der Kopfzeile und würden sonst den
       * Titel auf zwei Zeichen quetschen.
       */}
      {aktion && <div className="w-full lg:w-auto">{aktion}</div>}
    </div>
  )
}

/** Öffnet die Seitenleiste, solange sie eine Schublade ist. Ab `lg` steht sie ohnehin da. */
function MenueKnopf() {
  const { oeffnen } = useSchublade()
  return (
    <button
      type="button"
      onClick={oeffnen}
      aria-label="Menü öffnen"
      aria-controls="hauptnavigation"
      className="-ml-1.5 cursor-pointer rounded-lg p-2 text-ink2 hover:bg-[#f3f2ef] hover:text-ink lg:hidden"
    >
      <Menu className="size-[22px]" />
    </button>
  )
}

/**
 * Kein echtes Suchfeld: die Suche läuft über die ⌘K-Palette (Leitplanke 3).
 *
 * **Unter 1400 px nur noch ein Knopf** (Z-22, 20.10.2026, Nils: *„da oben müssten wir denke ich
 * aufräumen … wenn das display schmaler ist?"*). Das 250 px breite Feld war auf „Mein Tag" der
 * Grund, dass die Kopfzeile in drei Reihen umbrach. Am Rechner steht dort jetzt „⌘K 🔍", auf
 * dem Telefon nur die Lupe.
 * Der Knopf ist der sichtbare Hinweis darauf – wie im Mockup.
 *
 * **Zwei Knöpfe, nicht einer mit wechselndem Inhalt.** Am Rechner soll exakt dasselbe stehen
 * wie vorher – „⌘K  Suchen …" in 250 px, ohne Lupe. Auf dem Telefon ist beides sinnlos: das
 * Kürzel gibt es nicht, und 250 px sind zwei Drittel des Bildschirms. Ein gemeinsamer Knopf
 * hätte am Rechner ein Symbol dazubekommen, und das wäre eine sichtbare Änderung oberhalb der
 * Grenze – genau das, was dieses Paket ausschließt.
 */
function SuchAnstoss() {
  const { oeffnen } = usePalette()
  return (
    <>
      <button
        type="button"
        onClick={oeffnen}
        aria-label="Suchen"
        className="hidden w-[250px] shrink-0 cursor-pointer rounded-[10px] border border-line2 bg-surface px-[13px] py-2 text-left text-[13px] text-muted min-[1400px]:block"
      >
        ⌘K&nbsp;&nbsp;Suchen …
      </button>
      <button
        type="button"
        onClick={oeffnen}
        aria-label="Suchen"
        title="Suchen (⌘K)"
        className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[10px] border border-line2 bg-surface p-2 text-[12.5px] text-muted min-[1400px]:hidden"
      >
        <span className="max-sm:hidden">⌘K</span>
        <Search className="size-[18px]" />
      </button>
    </>
  )
}
